import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode } from "vue"

import { computed, ref } from "vue";
import {
  MembershipSchema,
  MembershipType,
  MemberTableProps,
  PermissionType,
} from "@/types";
import { membersColumns } from "@/util";
import {
  adminApiStore,
  memberApiStore,
  membersStore,
  permissionStore,
  projectStore,
  sessionStore,
} from "@/hooks";
import {
  PanelCard,
  SelectorTable,
  IconButton,
  TextButton,
  Typography,
} from "@/components/common";
import {
  InviteMemberInputs,
  MemberRoleButton,
} from "@/components/members/save";


/**
 * A table for managing members of a project, team, or organization.
 */
const __default__ = {
  name: "MemberTable",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    entity: {},
    minimal: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

const addedMember = ref<string | null>(null);
const addMode = ref(false);

const name = computed(() => props.entity.entityType?.toLowerCase() || "");
const itemName = computed(() => `${name.value} member`);

const entityType = computed(() => props.entity.entityType || "PROJECT");

const memberPermission = computed(
  () =>
    (
      ({
        PROJECT: "project.edit_members",
        TEAM: "team.edit_members",
        ORGANIZATION: "org.edit_members",
      }) as Record<MembershipType, PermissionType>
    )[entityType.value]
);

const displayMemberActions = computed(() =>
  permissionStore.isAllowed(
    memberPermission.value,
    permissionStore.getCurrentContext(entityType.value)
  )
);

const rows = computed(() => membersStore.getMembers(entityType.value));

const userEmail = computed(() => sessionStore.user?.email);

/**
 * Loads the project's members.
 */
async function handleRefresh(): Promise<void> {
  if (projectStore.projectId === "") return;

  await memberApiStore.handleReload(props.entity);
}

/**
 * Clears all member modal state.
 */
function handleClose(): void {
  addedMember.value = "";
  addMode.value = false;
}

/**
 * Opens the invite member modal.
 * @param email - The member email to invite.
 */
function handleAdd(email: string | null): void {
  addedMember.value = email;
  addMode.value = true;
}

/**
 * Opens the delete member modal, if the member is not the only owner.
 * @param member - The member to delete.
 */
function handleDelete(member: MembershipSchema): void {
  memberApiStore.handleDelete(member);
}

/**
 * Enables superuser access for the member.
 * @param member - The member to turn into a superuser.
 */
function handleSuperuser(member: MembershipSchema): void {
  adminApiStore.enableSuperuser(member);
}

return (_ctx: any,_cache: any) => {
  const _component_q_td = _resolveComponent("q-td")!

  return (_openBlock(), _createBlock(_unref(PanelCard), {
    minimal: props.minimal
  }, {
    "title-actions": _withCtx(() => [
      (addMode.value)
        ? (_openBlock(), _createBlock(_unref(TextButton), {
            key: 0,
            text: "",
            label: "Cancel",
            icon: "cancel",
            onClick: handleClose
          }))
        : _createCommentVNode("", true)
    ]),
    default: _withCtx(() => [
      (!addMode.value)
        ? (_openBlock(), _createBlock(_unref(SelectorTable), {
            key: 0,
            columns: _unref(membersColumns),
            rows: rows.value,
            "row-key": "email",
            "item-name": itemName.value,
            addable: "",
            deletable: displayMemberActions.value,
            loading: _unref(memberApiStore).loading,
            "custom-cells": ['role'],
            icons: { add: 'member-add', delete: 'member-delete', edit: 'edit' },
            "onRow:add": handleAdd,
            "onRow:delete": handleDelete,
            onRefresh: handleRefresh
          }, {
            "search-append": _withCtx(({ search }) => [
              (!!search)
                ? (_openBlock(), _createBlock(_unref(IconButton), {
                    key: 0,
                    small: "",
                    tooltip: "Invite member",
                    icon: "invite",
                    onClick: ($event: any) => (handleAdd(search))
                  }, null, 8, ["onClick"]))
                : _createCommentVNode("", true)
            ]),
            "body-cell-role": _withCtx(({ row }: { row: MembershipSchema }) => [
              _createVNode(_component_q_td, { align: "end" }, {
                default: _withCtx(() => [
                  (displayMemberActions.value)
                    ? (_openBlock(), _createBlock(_unref(MemberRoleButton), {
                        key: 0,
                        member: row
                      }, null, 8, ["member"]))
                    : (_openBlock(), _createBlock(_unref(Typography), {
                        key: 1,
                        value: row.role
                      }, null, 8, ["value"]))
                ]),
                _: 2
              }, 1024)
            ]),
            "cell-actions": _withCtx(({ row }: { row: MembershipSchema }) => [
              (_unref(permissionStore).isSuperuser)
                ? (_openBlock(), _createBlock(_unref(IconButton), {
                    key: 0,
                    icon: "security",
                    tooltip: "Create superuser",
                    "data-cy": "button-member-superuser",
                    onClick: ($event: any) => (handleSuperuser(row))
                  }, null, 8, ["onClick"]))
                : _createCommentVNode("", true),
              (row.email === userEmail.value)
                ? (_openBlock(), _createBlock(_unref(IconButton), {
                    key: 1,
                    icon: "leave",
                    tooltip: "Leave project",
                    "data-cy": "button-member-leave",
                    onClick: ($event: any) => (handleDelete(row))
                  }, null, 8, ["onClick"]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["columns", "rows", "item-name", "deletable", "loading"]))
        : (_openBlock(), _createBlock(_unref(InviteMemberInputs), {
            key: 1,
            entity: props.entity,
            email: addedMember.value,
            onSave: handleClose
          }, null, 8, ["entity", "email"]))
    ]),
    _: 1
  }, 8, ["minimal"]))
}
}

})